'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var _defineProperty = require('@babel/runtime/helpers/defineProperty');
var _toConsumableArray = require('@babel/runtime/helpers/toConsumableArray');
require('react');
var PropTypes = require('prop-types');
var react = require('@linaria/react');
var jsxRuntime = require('react/jsx-runtime');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var _defineProperty__default = /*#__PURE__*/_interopDefaultLegacy(_defineProperty);
var _toConsumableArray__default = /*#__PURE__*/_interopDefaultLegacy(_toConsumableArray);
var PropTypes__default = /*#__PURE__*/_interopDefaultLegacy(PropTypes);

/** Shameless clone of `next/image`'s srcSet generation */

// layout should probably be stripped and this defaulted to one version
var getWidths = function getWidths(_ref, width, layout, sizes) {
  var deviceSizes = _ref.deviceSizes,
    allSizes = _ref.allSizes;
  if (sizes && (layout === 'fill' || layout === 'responsive')) {
    // Find all the "vw" percent sizes used in the sizes prop
    var viewportWidthRe = /(^|\s)(1?\d?\d)vw/g;
    var percentSizes = [];
    // eslint-disable-next-line no-cond-assign
    for (var match; match = viewportWidthRe.exec(sizes); match) {
      percentSizes.push(parseInt(match[2]));
    }
    if (percentSizes.length) {
      var smallestRatio = Math.min.apply(Math, percentSizes) * 0.01;
      return {
        widths: allSizes.filter(function (s) {
          return s >= deviceSizes[0] * smallestRatio;
        }),
        kind: 'w'
      };
    }
    return {
      widths: allSizes,
      kind: 'w'
    };
  }
  if (typeof width !== 'number' || layout === 'fill' || layout === 'responsive') {
    return {
      widths: deviceSizes,
      kind: 'w'
    };
  }
  var widths = _toConsumableArray__default["default"](new Set(
  // This means that most OLED screens that say they are 3x resolution,
  // are actually 3x in the green color, but only 1.5x in the red and
  // blue colors. Showing a 3x resolution image in the app vs a 2x
  // resolution image will be visually the same, though the 3x image
  // takes significantly more data. Even true 3x resolution screens are
  // wasteful as the human eye cannot see that level of detail without
  // something like a magnifying glass.
  // https://blog.twitter.com/engineering/en_us/topics/infrastructure/2019/capping-image-fidelity-on-ultra-high-resolution-devices.html
  // [width, width * 2 , width * 3].map(
  [width, width * 2].map(function (w) {
    return allSizes.find(function (p) {
      return p >= w;
    }) || allSizes[allSizes.length - 1];
  })));
  return {
    widths: widths,
    kind: 'x'
  };
};
var generateImageSrcSet = function generateImageSrcSet(_ref2) {
  var config = _ref2.config,
    src = _ref2.src,
    layout = _ref2.layout,
    width = _ref2.width,
    quality = _ref2.quality,
    sizes = _ref2.sizes,
    loader = _ref2.loader;
  var _getWidths = getWidths(config, width, layout, sizes),
    widths = _getWidths.widths,
    kind = _getWidths.kind;
  var last = widths.length - 1;
  return {
    sizes: !sizes && kind === 'w' ? '100vw' : sizes,
    srcSet: widths.map(function (w, i) {
      return "".concat(loader({
        src: src,
        quality: quality,
        width: w
      }), " ").concat(kind === 'w' ? w : i + 1).concat(kind);
    }).join(', '),
    // It's intended to keep `src` the last attribute because React updates
    // attributes in order. If we keep `src` the first one, Safari will
    // immediately start to fetch `src`, before `sizes` and `srcSet` are even
    // updated by React. That causes multiple unnecessary requests if `srcSet`
    // and `sizes` are defined.
    // This bug cannot be reproduced in Chrome or Firefox.
    src: loader({
      src: src,
      quality: quality,
      width: widths[last]
    })
  };
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty__default["default"](target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
// The type being `NextImage` is fine since it extends img

// These defaults are if none are supplied to the non-next component
var defaultDeviceSizes = [640, 750, 828, 1080, 1200, 1920, 2048, 3840];
var defaultImageSizes = [16, 32, 48, 64, 96, 128, 256, 384, 512];
var defaultLoader = function defaultLoader(resolverProps) {
  return resolverProps.src;
};
var ImgPropTypes = {
  /** Source of the image to load */
  src: PropTypes__default["default"].string.isRequired,
  /** Alt text for the image */
  alt: PropTypes__default["default"].string.isRequired,
  /** Width of the image */
  width: PropTypes__default["default"].number.isRequired,
  /** Height of the image */
  height: PropTypes__default["default"].number.isRequired,
  /** Additional classes to add to the image */
  className: PropTypes__default["default"].string,
  // `img` specific props, should not be required
  /** Used to define images at different sizes */
  srcSet: PropTypes__default["default"].bool,
  /** Width of images for different sizes */
  sizes: PropTypes__default["default"].string,
  /** Sizes of devices being used to view the image */
  deviceSizes: PropTypes__default["default"].array,
  /** Sizes of the images being rendered */
  imageSizes: PropTypes__default["default"].array,
  // `next/image` specific props used in regular `img`
  /** Determines how the image will be rendered */
  layout: PropTypes__default["default"].oneOf(['fill', 'fixed', 'intrinsic', 'responsive']),
  /** Defines which loader to use */
  loader: PropTypes__default["default"].func,
  // <NextImageLoader>

  // `next/image` specific props, should not be required, add to this list as needed
  /** Decides what to show for the image when it is not available */
  placeholder: PropTypes__default["default"].oneOf(['blur', 'empty']),
  /** Enables priority for the image */
  priority: PropTypes__default["default"].bool,
  /** Configure Lazy or Eager loading */
  loading: PropTypes__default["default"].oneOf(['lazy', 'eager']),
  /** Function to run when loading is complete */
  onLoadingComplete: PropTypes__default["default"].func,
  /** Test Id to use in jest tests */
  jestTestId: PropTypes__default["default"].string
};
var _exp = function _exp() {
  return "img";
};
var StyledImg = /*#__PURE__*/react.styled(_exp())({
  name: "StyledImg",
  "class": "smakcs6",
  propsAsIs: true
});

// Built in loaders for GR specific CDNs
// https://nextjs.org/docs/api-reference/next/image#loader
var loaders = [
// ContentStack
{
  re: new RegExp(/https?:\/\/images-cs\.rate\.com/),
  loader: function loader(_ref) {
    var src = _ref.src,
      width = _ref.width,
      quality = _ref.quality;
    var url = new URL(src);
    url.searchParams.set('width', width.toString());
    url.searchParams.set('quality', (quality === null || quality === void 0 ? void 0 : quality.toString()) || '75');
    return url.href;
  }
}
// Cloudfront / S3
// Can image manipulate with lambda functions
// https://aws.amazon.com/blogs/networking-and-content-delivery/resizing-images-with-amazon-cloudfront-lambdaedge-aws-cdn-blog/
// {
//   re: new RegExp(/https?:\/\/*.cloudfront.net/),
//   loader: ({ src, width, quality }) => {
//     const url = new URL(src);
//     url.searchParams.set('width', width.toString());
//     url.searchParams.set('quality', quality?.toString() || '75');
//     return url.href;
//   }
// },
];

var Img = function Img(_ref2) {
  var _loaders$find;
  var src = _ref2.src,
    alt = _ref2.alt,
    width = _ref2.width,
    height = _ref2.height,
    className = _ref2.className,
    _ref2$srcSet = _ref2.srcSet,
    srcSet = _ref2$srcSet === void 0 ? false : _ref2$srcSet,
    sizes = _ref2.sizes,
    deviceSizes = _ref2.deviceSizes,
    imageSizes = _ref2.imageSizes,
    layout = _ref2.layout,
    loader = _ref2.loader;
    _ref2.placeholder;
    _ref2.priority;
    _ref2.loading;
    _ref2.onLoadingComplete;
    var jestTestId = _ref2.jestTestId;
  // `NextImageProps` is an extension `JSX.IntrinsicElements['img']` with
  // certain properties omitted. Here we add the ones we want back in for
  // compatibility with the react image component.
  var props = {
    src: src,
    alt: alt,
    width: width,
    height: height,
    className: className !== null && className !== void 0 ? className : undefined
  };

  // loader
  var foundLoader = loader || ((_loaders$find = loaders.find(function (_ref3) {
    var re = _ref3.re;
    return !!re.test(src);
  })) === null || _loaders$find === void 0 ? void 0 : _loaders$find.loader);
  {
    // Generate a valid srcset attributes. Currently not based on a layout.
    // eslint-disable-next-line no-lonely-if
    if (sizes || srcSet) {
      // Generates
      var allSizes = [].concat(_toConsumableArray__default["default"](deviceSizes !== null && deviceSizes !== void 0 ? deviceSizes : defaultDeviceSizes), _toConsumableArray__default["default"](imageSizes !== null && imageSizes !== void 0 ? imageSizes : defaultImageSizes)).sort(function (a, b) {
        return a - b;
      });
      var sortedDeviceSizes = defaultDeviceSizes.sort(function (a, b) {
        return a - b;
      });
      var srcSetConfig = {
        allSizes: allSizes,
        deviceSizes: sortedDeviceSizes,
        src: src,
        width: width
      };
      props = _objectSpread(_objectSpread({}, props), generateImageSrcSet({
        config: srcSetConfig,
        src: src,
        layout: layout !== null && layout !== void 0 ? layout : undefined,
        width: width,
        sizes: sizes !== null && sizes !== void 0 ? sizes : '100vw',
        loader: foundLoader || defaultLoader
      }));
    }
  }
  return /*#__PURE__*/jsxRuntime.jsx(StyledImg, _objectSpread({
    "data-testid": jestTestId || 'styled-img'
  }, props));
};
Img.propTypes = ImgPropTypes;

exports.ImgPropTypes = ImgPropTypes;
exports["default"] = Img;
